import {
  ApplicationRef,
  Component,
  Inject,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatInput } from '@angular/material/input';

import { ActivatedRoute, Router } from '@angular/router';
import {
  AcceptableFileTypes,
  FileSelected,
  Image,
  IS_SERVER_PLATFORM,
  PDFReportInformation,
  PrintColorsConstant,
  PrintOrderConstants,
  StockItem,
} from 'src/app/core';
import { printOrderAnimations } from '../../animation';
import { IntroService, OrderTypes, rangeValidator } from 'src/app/shared';
import {
  AnimationBuilderService,
  BindingsService,
  FilesService,
  MainLogoAnimService,
  OrderService,
  PrintBlockService,
  StockService,
  TitleService,
  TypingsService,
} from 'src/app/feature/services';
import { BreakpointObserver } from '@angular/cdk/layout';
import { PrintOrderComponent } from '../print-order/print-order.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FilePlaceholderComponent } from 'src/app/feature/components';
import { ComponentBusService } from '../../services/component-bus.service';
import { DataSharingService } from 'src/app/shared/services/data-sharing.service';
import { config } from '../../../../../../config/config';

const _ = require('lodash');

export type TypingsType = { name: string; items: StockItem[] }[];
@Component({
  selector: 'thesis-order',
  templateUrl: './thesis-order.component.html',
  styleUrls: [
    './thesis-order.component.scss',
    '../print-order/print-order.component.scss',
  ],
  animations: printOrderAnimations,
})
export class ThesisOrderComponent
  extends PrintOrderComponent
  implements OnInit
{
  @ViewChild('copiesInput') copiesInput!: MatInput;

  @ViewChild('signatureFilePlaceholder')
  signatureFileHolder!: FilePlaceholderComponent;

  @ViewChild('coverImageFilePlaceholder')
  coverFilePlaceholder!: FilePlaceholderComponent;

  private selectedImages: FileSelected[] = [];
  public acceptableFileUploadTypes: AcceptableFileTypes[] = [
    'image/jpeg',
    'image/png',
  ];

  public uploadingImage: boolean = false;
  public coverImageUploaded: boolean = false;
  public instructorSignatureUploaded: boolean = false;

  public coverImage!: Image;
  public instructorSignatureImage!: Image;

  /**
   * Activated part of component controlled
   */
  public activatedState: 'thesis' | 'edit' = 'thesis';

  constructor(
    animationBuilder: AnimationBuilderService,
    busService: ComponentBusService,
    mainLogoAnimation: MainLogoAnimService,
    breakpointObserver: BreakpointObserver,
    printBlockService: PrintBlockService,
    bindingsService: BindingsService,
    typingsService: TypingsService,
    activatedRoute: ActivatedRoute,
    introJSService: IntroService,
    titleService: TitleService,
    filesService: FilesService,
    orderService: OrderService,
    stockService: StockService,
    snackbar: MatSnackBar,
    dataSharingService: DataSharingService,

    @Inject(IS_SERVER_PLATFORM) isServer: boolean,

    router: Router,
    _ngZone: NgZone,
    appRef: ApplicationRef
  ) {
    super(
      router,
      _ngZone,
      filesService,
      titleService,
      orderService,
      typingsService,
      bindingsService,
      printBlockService,
      breakpointObserver,
      snackbar,
      mainLogoAnimation,
      activatedRoute,
      animationBuilder,
      isServer,
      stockService,
      introJSService,
      busService,
      dataSharingService
    );
    // this.titleService.setTitle(this._title);
    if (!isServer) this.introJs = this.introJsService.createInstance();
  }
  public printTypesConst = PrintColorsConstant;

  /** Main form field. */
  override orderFormGroup = new FormGroup({
    pageNo: new FormControl(),
    printAll: new FormControl(),
    binding: new FormControl(0),
    twoSided: new FormControl(true),
    talcColor: new FormControl(''),
    moreDetails: new FormControl(''),
    customPageNo: new FormControl(),
    range: new FormControl('', [rangeValidator]),
    copies: new FormControl('1', [Validators.required, Validators.min(1)]),
    printSize: new FormControl(
      PrintOrderConstants.DEFAULT_PRINT_SIZE,
      Validators.required
    ),
    caseType: new FormControl(
      PrintOrderConstants.DEFAULT_CASETYPE_VALUE,
      Validators.required
    ),
    pageColor: new FormControl(PrintColorsConstant.MIXED as any, [
      Validators.required,
    ]),
  });

  /**
   * Override the getAnimationOffset
   * @returns {number} animation offset
   */

  public override getAnimationOffset(): number {
    return 1400;
  }

  override getPrintType(): OrderTypes {
    return 'thesis';
  }

  protected override getBinding(): StockItem | undefined {
    return this.bindings.find((o) => o.id === this.binding?.value);
  }

  public selectImage(
    name: 'signature' | 'cover',
    selectedImage:
      | { file: Image[] | any; name: string }
      | PDFReportInformation
      | any
  ) {
    if (!selectedImage) {
      throw new Error('No image is loaded');
    }

    switch (name) {
      case 'signature':
        this.instructorSignatureImage = selectedImage.file[0];
        break;

      case 'cover':
        this.coverImage = selectedImage.file[0];
        break;

      default:
        break;
    }
  }

  removeImageFromSelectedImages(selectedImage: FileSelected) {
    const index = this.selectedImages.findIndex((image) => {
      return image.name === selectedImage.name;
    });

    if (index >= 0) this.selectedImages.splice(index, 1);
  }

  public changeStateTo(state: 'thesis' | 'edit') {
    this.activatedState = state;
  }

  public override getImages(): number[] {
    const images = [];

    if (this.coverFilePlaceholder.checked) {
      images.push(this.coverImage.id);
    }

    if (this.signatureFileHolder.checked) {
      images.push(this.instructorSignatureImage.id);
    }
    return images;
  }

  public override ngAfterViewInit(): void {
    if (!this.isServer) {
      this.introJs.setOptions({
        nextLabel: 'بعدی',
        prevLabel: 'قبلی',
        doneLabel: 'اتمام',
        scrollToElement: false,
      });
  
      this.introJs.addSteps([
        {
          element: '#uploadMain .dropzone',
          title: 'آپلود فایل',
          intro: 'با انتخاب این گزینه، صفحه‌ای برای شما باز خواهد شد.<br/>فایل خود را به آن بکشید یا فایل مورد نظر را انتخاب کنید.',
        },
  
        {
          element: '#printColor .mdc-notched-outline',
          title: 'نوع چاپ',
          intro: 'نوع چاپ مورد نظر را انتخاب کنید. <br/>در صورت انتخاب چاپ ترکیبی، صفحات نیمه رنگی و تمام رنگی مورد نظر را در باکس پایین انتخاب کنید.'
        },
  
        {
          element: '#printBtnToggle',
          title: 'یک رو / دو رو',
          intro: 'در صورت انتخاب گزینه دو رو، هر دو صفحه از فایل شما بر روی یک برگ کاغذ به‌صورت پشت و رو چاپ خواهد شد.',
        },
  
        {
          element: '#pageRange .page-count .mdc-notched-outline',
          title: 'تعداد صفحات',
          intro: 'تعداد صفحات مورد نظر از فایل انتخابی را مشخص کنید.<br/>مثال: اگر عدد 90 را وارد کنید، از صفحه 1 تا 90 فایل آپلود شده، پرینت خواهد شد.<br/>*برای انتخاب بازه‌ای از صفحات، آن را در قسمت «توضیحات» وارد کنید.',
        },
  
        {
          element: '#pageRange .print-all',
          title: 'همه صفحات',
          intro: 'با انتخاب این گزینه، کل صفحات فایل مورد نظر پرینت خواهد شد.',
        },
  
        {
          element: '#copiesNumber .mdc-notched-outline',
          title: 'تعداد نسخه',
          intro: 'اگر می‌خواهید از یک فایل چند نسخه پرینت شود، تعداد نسخه مورد نظر را در این قسمت وارد کنید.'

        },
        {
          element: '#printBinding .mdc-notched-outline',  
          title: 'انتخاب صحافی',
          intro: 'نوع صحافی مورد نظر خود را انتخاب کنید.<br/>با انتخاب گزینه کاور، سفارش شما در کاور پلاستیکی قرار داده می‌شود.',
        },
  
        {
          element: '#coverImagePlaceholder .file-upload-placeholder-wrapper',
          title: 'طرح روی جلد',
          intro: 'در صورت  انتخاب صحافی گالینگور، طرح جلد مورد نظر خود را آپلود کرده و سپس این گزینه را فعال کنید.',
        },
  
        {
          element: '#talcColorSelector',
          title: 'رنگ طلق / جلد',
          intro: 'رنگ طلق یا جلد صحافی مورد نظر خود را انتخاب کنید',
        },
  
        {
          element:
            '#instructorSignaturePlaceholder .file-upload-placeholder-wrapper',
          title: 'صفحه امضای استاد',
          intro: 'در صورت تمایل به اضافه‌کردن امضای استاد، ابتدا فایل امضا را آپلود کرده و سپس این گزینه را فعال کنید.',
        },
  
        {
          element: '#halfColorRangeSelect .item-list',
          title: 'صفحات نیمه رنگی',
          intro: 'بازه صفحات نیمه‌ رنگی (مثال: 20 الی 10) را وارد کرده و سپس علامت + را انتخاب کنید.<br/>یا در قسمت «تک صفحه»، شماره هر صفحه را وارد کرده و علامت + را بزنید.<br/>می‌توانید چند بازه و یا چند تک‌برگ انتخاب کنید.'
        },
  
        {
          element: '#fullColorRangeSelect .item-list',
          title: 'صفحات تمام رنگی',
          intro: 'بازه صفحات تمام رنگی (مثال: 35 الی 25) را وارد کرده و سپس علامت + را انتخاب کنید.<br/>یا در قسمت «تک صفحه»، شماره هر صفحه را وارد کرده و علامت + را بزنید.</br>می‌توانید چند بازه و یا چند تک‌برگ انتخاب کنید.',
        },
  
        {
          element: '#printOrderExtraInformation .mdc-notched-outline',
          title: 'توضیحات',
          intro: 'هر توضیح دیگری دررابطه‌با سفارش خود دارید، در این قسمت وارد کنید.',
        },
  
        {
          element: '#totalPriceSection',
          title: 'قیمت',
          intro:'در این قسمت می‌توانید قیمت را به صورت زنده با تغییر گزینه‌ها ببینید',
        },
  
        {
          element: '#submitBtn',
          title: 'ثبت سفارش',
          intro: 'با کلیک روی این قسمت، شما می‌توانید سفارش را ثبت کنید. بعد از ثبت سفارش، خرید شما در قسمت سبد خرید قابل مشاهده خواهد بود.',
        },
  
        {
          element: '#tarefeActionButton',
          title: 'تعرفه‌ها',
          intro: 'با کلیک روی این دکمه، می‌توانید تعرفه‌های مختلف را مشاهده کنید.',
        },
      ]);
    }
  }

  // Private methods ------------------------------------------>

  protected override setupAnimation() {
    this.mainLogoAnimation.updateHeight(1250);
  }
}
